import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Loader2, Shield, User } from 'lucide-react';

export function UsersAndQuota() {
  const [users, setUsers] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form state for User
  const [id, setId] = useState<string | undefined>(undefined);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, plansRes] = await Promise.all([
        api.getAdminUsers(),
        api.getBillingPlans()
      ]);
      setUsers(usersRes);
      setPlans(plansRes);
    } catch (e: any) {
      alert("Error loading data: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveUser = async () => {
    if (!username || !role) {
      alert("Username and Role are required");
      return;
    }
    try {
      await api.createOrUpdateAdminUser({ 
        id, 
        username, 
        password: password || undefined, 
        display_name: displayName, 
        email, 
        role 
      });
      setId(undefined);
      setUsername('');
      setPassword('');
      setDisplayName('');
      setEmail('');
      setRole('user');
      await loadData();
    } catch (e: any) {
      alert("Error saving user: " + e.message);
    }
  };

  const handleEditUser = (u: any) => {
    setId(u.id);
    setUsername(u.username);
    setPassword(''); // don't load hash
    setDisplayName(u.display_name || '');
    setEmail(u.email || '');
    setRole(u.role);
  };

  const handleDeleteUser = async (uId: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${uId}?`)) return;
    try {
      await api.deleteAdminUser(uId);
      await loadData();
    } catch (e: any) {
      alert("Error deleting user: " + e.message);
    }
  };

  const handleAssignPlan = async (userId: string, planId: string) => {
    if (!planId) return;
    try {
      const res = await api.assignUserPlan(userId, planId);
      alert(`Successfully added ${res.added} credits!`);
      await loadData();
    } catch (e: any) {
      alert("Error assigning plan: " + e.message);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', color: '#fff' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 8px 0' }}>Users & Quota</h1>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>Manage user accounts and credit allocations</p>
      </div>

      <div style={{ 
        background: 'var(--bg-elevated)', 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '14px', fontWeight: '600' }}>{id ? 'Update User' : 'Create User'}</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Username</label>
            <input 
              className="input-field"
              type="text" 
              value={username}
              onChange={e => setUsername(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Password {id && '(Leave blank to keep)'}</label>
            <input 
              className="input-field"
              type="password" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1.5', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Display Name</label>
            <input 
              className="input-field"
              type="text" 
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1.5', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Email</label>
            <input 
              className="input-field"
              type="email" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '0.8', minWidth: '100px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Role</label>
            <select 
              className="input-field" 
              value={role} 
              onChange={e => setRole(e.target.value)}
              style={{ width: '100%', appearance: 'auto', background: 'var(--bg-elevated)' }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button 
            className="btn btn--primary" 
            onClick={handleSaveUser}
            style={{ 
              height: '38px', 
              background: 'linear-gradient(135deg, #6366f1, #06b6d4)', 
              border: 'none',
              boxShadow: '0 0 10px rgba(99, 102, 241, 0.4)'
            }}
          >
            {id ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      <div style={{ 
        background: 'var(--bg-elevated)', 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loader2 className="spin" size={24} style={{ opacity: 0.5 }} />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>USER</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>ROLE</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>CURRENT PLAN</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>TOKEN BALANCE</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ fontWeight: '600', color: '#fff' }}>{u.display_name || u.username}</div>
                    {(u.display_name && u.email) && (
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>{u.email}</div>
                    )}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <span className={`badge badge--${u.role === 'admin' ? 'purple' : 'blue'}`}>
                      {u.role === 'admin' ? <Shield size={12} style={{ marginRight: '2px' }} /> : <User size={12} style={{ marginRight: '2px' }} />}
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>
                    {u.plan_id || 'N/A'}
                  </td>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>
                    {Number(u.tokens_balance).toLocaleString()}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select 
                        className="input-field select-action" 
                        onChange={e => {
                          if(e.target.value) {
                            handleAssignPlan(u.id, e.target.value);
                            e.target.value = ''; // reset selection
                          }
                        }}
                        style={{ 
                          padding: '4px 12px', height: '32px', fontSize: '12px', 
                          appearance: 'auto', background: 'rgba(255,255,255,0.05)', 
                          border: '1px solid rgba(255,255,255,0.2)', color: '#fff', 
                          borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', outline: 'none' 
                        }}
                      >
                        <option value="" style={{ background: '#14142e' }}>Assign Plan (Add Credit)</option>
                        {plans.map(p => (
                          <option key={p.id} value={p.id} style={{ background: '#14142e' }}>{p.name} (+{Number(p.token_credit).toLocaleString()})</option>
                        ))}
                      </select>
                      <button 
                        className="btn" 
                        style={{ 
                          background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.4)', 
                          color: '#67e8f9', height: '32px', padding: '0 12px', borderRadius: '6px', 
                          fontSize: '12px', fontWeight: '600', boxShadow: '0 0 10px rgba(6,182,212,0.2)',
                          textShadow: '0 0 5px rgba(103,232,249,0.5)', transition: 'all 0.2s'
                        }}
                        onClick={() => handleEditUser(u)}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 0 15px rgba(6,182,212,0.5)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = '0 0 10px rgba(6,182,212,0.2)'}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn" 
                        style={{ 
                          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', 
                          color: '#fca5a5', height: '32px', padding: '0 12px', borderRadius: '6px', 
                          fontSize: '12px', fontWeight: '600', boxShadow: '0 0 10px rgba(239,68,68,0.2)',
                          textShadow: '0 0 5px rgba(252,165,165,0.5)', transition: 'all 0.2s'
                        }}
                        onClick={() => handleDeleteUser(u.id)}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 0 15px rgba(239,68,68,0.5)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = '0 0 10px rgba(239,68,68,0.2)'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
